import { faHeartPulse, faUserGroup } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Col, Form, Row, Card, Modal, Button, Typography } from 'antd'
import React, { useEffect, useState, useRef } from 'react'
import MedicalHistoryFormTemplates from '../../utils/templates'
import DesktopMedicalHistoryNav from './Nav'
import useFamilyHistory from '../../hooks/useFamilyHistory'
import '../style/DesktopFamilyHistory.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopFamilyHistory = ({ clientForm, setFacet, setSaving }) => {
  const { handleOptionClick, handleSubmit, selectedConditions } = useFamilyHistory({ clientForm, setFacet, setSaving })

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Capture initial state when component loads - with delay to ensure state is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialState.current && selectedConditions) {
        initialState.current = [...selectedConditions]
        console.log('Initial family history state captured:', initialState.current)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [selectedConditions])

  // Clear initial state when component unmounts
  useEffect(() => {
    return () => {
      initialState.current = null
    }
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current) return false

    const hasChanges = JSON.stringify(selectedConditions) !== JSON.stringify(initialState.current)
    console.log('Family history change detection:', {
      hasChanges,
      initial: initialState.current,
      current: selectedConditions
    })

    return hasChanges
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = [...selectedConditions]
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSubmit that also updates initial state
  const handleSaveWithStateUpdate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle navigation without saving
  const handleNavigateWithoutSaving = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  return (
    <div className='desktop-family-history-container'>
      <DesktopSectionHeader
        icon={faUserGroup}
        title='Family History'
        subtitle='Do any of your immediate family members have these conditions?'
        gradient='linear-gradient(135deg, #F1A63B 0%, #F6C15C 100%)'
      />

      <div className='desktop-family-history-content'>
        <Card className='desktop-conditions-card'>
          <Row gutter={[16, 16]}>
            {MedicalHistoryFormTemplates.dataModel.familyHistoryList.options.map((condition, index) => {
              const isSelected = selectedConditions.includes(condition.value)

              return (
                <Col xs={24} sm={12} md={8} lg={6} key={condition.value}>
                  <div
                    className={`desktop-condition-option ${isSelected ? 'selected' : ''}`}
                    role='checkbox'
                    onClick={() => handleOptionClick(condition)}
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleOptionClick(condition)
                      }
                    }}
                  >
                    <div className='desktop-condition-icon'>
                      <FontAwesomeIcon icon={faHeartPulse} />
                    </div>
                    <span className='desktop-condition-name'>{condition.label}</span>

                    <Form.Item name={['familyHistory', 'list']} style={{ display: 'none' }}>
                      <input type='checkbox' />
                    </Form.Item>
                  </div>
                </Col>
              )
            })}
          </Row>
        </Card>
      </div>

      <DesktopMedicalHistoryNav
        prevFunction={() =>
          handleNavigation(() => {
            clientForm.resetFields()
            setFacet(null)
          })
        }
        nextFunction={handleSaveWithStateUpdate}
        prevText='Back to Overview'
        nextText='Save Family History'
      />

      {/* Unsaved Changes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faUserGroup} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your family history information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button type='primary' className='desktop-save-changes-btn' onClick={handleSaveAndNavigate} loading={false}>
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default DesktopFamilyHistory
